import { Icon } from '@natera/material/lib/icon'
import { useEffect, useState } from 'react'
import { useIsFeatureEnabled } from '~/shared/lib/feature-flags'
import { IconButton, ScrollArea, Text } from '~/shared/ui'
import {
  selectHasNonTerminal,
  selectItems,
  selectSuccessCount,
  selectTotalBatchCount,
  useUploadQueueStore,
} from '../../model/use-upload-queue-store'
import { UploadProgressRow } from './upload-progress-row'

export function UploadProgressPopup(): React.ReactElement | null {
  const isEnabled = useIsFeatureEnabled('2026-05-documents-uploading')
  const items = useUploadQueueStore(selectItems)
  const total = useUploadQueueStore(selectTotalBatchCount)
  const successCount = useUploadQueueStore(selectSuccessCount)
  const hasNonTerminal = useUploadQueueStore(selectHasNonTerminal)
  const [isCollapsed, setIsCollapsed] = useState(false)

  useEffect(() => {
    if (!hasNonTerminal) return
    const handleBeforeUnload = (e: BeforeUnloadEvent): void => {
      e.preventDefault()
    }
    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
    }
  }, [hasNonTerminal])

  if (!isEnabled) return null
  if (items.length === 0) return null

  const handleDismiss = (): void => {
    if (hasNonTerminal) return
    useUploadQueueStore.getState().dismissPopup()
  }

  const handleToggleCollapse = (): void => {
    setIsCollapsed((prev) => !prev)
  }

  return (
    <div className="fixed right-6 bottom-12 z-50 flex w-[280px] flex-col overflow-hidden rounded-md bg-white shadow-lg">
      <div className="bg-surface-low flex items-center justify-between px-3 py-2">
        <Text
          appearance="SUBTITLE2"
          className="text-text-primary"
        >
          {hasNonTerminal
            ? `Uploading ${total} file${total === 1 ? '' : 's'}`
            : `Uploaded ${successCount} file${successCount === 1 ? '' : 's'}`}
        </Text>
        <div className="flex items-center gap-1">
          <IconButton
            size="small"
            container="small"
            onClick={handleToggleCollapse}
            aria-label={isCollapsed ? 'Expand upload progress' : 'Collapse upload progress'}
          >
            <Icon children={isCollapsed ? 'expand_less' : 'expand_more'} />
          </IconButton>
          {!hasNonTerminal && (
            <IconButton
              size="small"
              container="small"
              onClick={handleDismiss}
              aria-label="Close upload progress"
            >
              <Icon children="close" />
            </IconButton>
          )}
        </div>
      </div>
      {!isCollapsed && (
        <ScrollArea>
          <div className="max-h-[210px]">
            {items.map((item) => (
              <UploadProgressRow
                key={item.id}
                item={item}
              />
            ))}
          </div>
        </ScrollArea>
      )}
    </div>
  )
}
